import { showError } from "./ErrorPopup";
import React, { useState, useEffect } from "react";
import { CheckCircleIcon } from "@heroicons/react/solid";
import ReassignerGroup from "./ReassignerGroup";
import { useNavigate } from "react-router-dom";
import { initLightboxJS } from "lightbox.js-react";
import { SlideshowLightbox } from "lightbox.js-react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Search,
  UserRoundCheck,
  X,
} from "lucide-react";
import { apiFetch } from '../utils/apiClient';
import ModernMonthPicker from './ModernMonthPicker';

function ResponsiveDetailsTable({ columns, rows, getRowKey }) {
  return (
    <>
      <div className="space-y-3 sm:hidden">
        {rows.map((row, rowIndex) => (
          <div
            key={getRowKey?.(row, rowIndex) ?? rowIndex}
            className="overflow-hidden rounded-lg border border-slate-200 bg-white"
          >
            <dl className="divide-y divide-slate-100">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 px-3 py-2.5"
                >
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {column.label}
                  </dt>
                  <dd className="min-w-0 whitespace-pre-wrap break-words text-sm leading-5 text-slate-800 [overflow-wrap:anywhere]">
                    {column.render(row) || "—"}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <div className="hidden max-w-full overflow-hidden rounded-lg border border-slate-200 sm:block">
        <div className="max-w-full overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed bg-white">
            <thead className="bg-slate-100">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((row, rowIndex) => (
                <tr key={getRowKey?.(row, rowIndex) ?? rowIndex}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="whitespace-pre-wrap break-words px-3 py-3 align-top text-sm leading-5 text-slate-700 [overflow-wrap:anywhere]"
                    >
                      {column.render(row) || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function TachesEnAttente({
  user,
  socket,
  setAuthLoading,
  authLoading,
  demandeEnAttente,
  checkGroupAdmin,
  checkGroupRecMensuelle,
  checkGroupSeancePlacement,
  checkGroupDemandeProjet,
  formAccess,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  filterValue,
  setFilterValue,
  currentPage,
  setCurrentPage,
  totalPages,
  setTotalPages,
  filterNom,
  setFilterNom,
  acceptedByFilter,
  setAcceptedByFilter,
  nomFormulaire,
  setNomFormulaire,
  periodeReclamation,
  setPeriodeReclamation,
  statusFilter,
  setStatusFilter,
  lieuFilter,
  setLieuFilter,
  assigneFilter,
  setAssigneFilter,
  messageErreurNom,
  messageErreur,
  messageErreurDate,
  setShouldFetch
}) {
  const canAccessManagedForm = (formKey) =>
    formAccess?.restricted === false ||
    formAccess?.allowedForms?.includes(formKey) === true;

  const [selectedForm, setSelectedForm] = useState(null);
  const [derniereDemandeSelectionnee, setDerniereDemandeSelectionnee] = useState(null);
  const [isAccepted, setIsAccepted] = useState(false);
  const [showReject, setshowReject] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showComment, setShowComment] = useState("");
  const [errorShowComment, setErrorShowComment] = useState("");
  const [message, setMessage] = useState({ message: "", color: "" });
  const [showConfirm, setshowConfirm] = useState(false);
  const [showReassignPopup, setShowReassignPopup] = useState(false);
  const [errorReassign, setErrorReassign] = useState("");
  const [emailUser, setEmailUser] = useState("");
  const [nomUser, setNomUser] = useState("");
  const [idUser, setIdUser] = useState("");
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  // const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [recMensuelleGroupUserIds, setRecMensuelleGroupUserIds] = useState([]);
  const [excelDataEcole, setExcelDataEcole] = useState([]);
  const [listSpUsers, setListSpUsers] = useState([]);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,        // "processingDate" | "submissionDate"
    order: "asc",    // "asc" | "desc"
  });


  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/getUsers?nomFormulaire=${"Séance de placement"}`);
        
        const data = await response.json();

        if (!response.ok) {
        throw new Error(data.error || data.message);
      }

        setListSpUsers(data);
      } catch (error) {
      console.error(error);
    }
    };

    // Initial fetch when the component mounts
    fetchRoles();
  }, []);

  useEffect(() => {
    // Si aucun des deux n'est vrai, on ne fait rien
    if (!checkGroupSeancePlacement && !checkGroupAdmin) return;

    const fetchExcelData = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/listeDesEcoles`
        );
        
        const result = await response.json();

        if (!response.ok) {
        throw new Error(result.error || result.message);
      }
        
        setExcelDataEcole(result.data || []);

      } catch (error) {
      console.error(error);
    }
    };

    fetchExcelData();
  }, [checkGroupSeancePlacement, checkGroupAdmin]); // ⚡ dépendances


  useEffect(() => {
    const fetchGroupUsers = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/getUsersInRecMensuelleGroup`
        );
        const data = await response.json();
         if (!response.ok) {
        throw new Error(data.error || data.message);
      }
        setRecMensuelleGroupUserIds(data.userIds || []);
      } catch (error) {
      console.error(error);
    }
    };

    fetchGroupUsers();
  }, []);

  useEffect(() => {
    initLightboxJS("Insert_Your_License_Key_Or_Use_Default", "default");
  }, []);

  useEffect(() => {
  if (
    uploadedFile?.contentType === "application/pdf" &&
    uploadedFile?.data
  ) {
    try {

      //base64 → binaire → Blob → URL

      //Convertit le base64 → texte binaire
      const byteCharacters = atob(uploadedFile.data); 
      //Convertit chaque caractère en code numérique (byte)
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      //Transforme les nombres en vrai format binaire utilisable
      const byteArray = new Uint8Array(byteNumbers);

      //Crée un fichier PDF réel dans le navigateur
      const blob = new Blob([byteArray], {
        type: "application/pdf",
      });

      //Crée un lien temporaire vers ce fichier
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      return () => URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur conversion PDF:", err);
    }
  }
}, [uploadedFile]);

  const fetchUploadedFile = async (numeroFormulaire) => {
  setLoadingFile(true);

  try {
    const response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/uploadedFile/${numeroFormulaire}`
    );

    const data = await response.json(); // 🔥 TRÈS IMPORTANT
     if (!response.ok) {
        throw new Error(data.error || data.message);
      }

    setUploadedFile(data.uploadedFile);
    // setShowPopup(true);
    // ❌ Ne pas ouvrir le popup si ça finit par "-DP"
    if (!numeroFormulaire?.endsWith("-DP")) {
      setShowPopup(true);
    }
    return data.uploadedFile;
  } catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    } finally {
    setLoadingFile(false);
  }
};

  const handleContinueClick = async (traite) => {
    if (!selectedForm) return;

    const { nomFormulaire } = selectedForm;

    switch (nomFormulaire) {
      

      case "Entente de travail en mode hybrid":
      case "Réclamation mensuelle de dépenses":
      case "Achat équipement informatique": {
        setIsLoading(true);

        const formPath = {
          "Entente de travail en mode hybrid": "/EntenteTravailHybrid",
          "Réclamation mensuelle de dépenses": "/formulaire",
          "Achat équipement informatique": "/achatEquipInfo",
        }[nomFormulaire];
        const url = `${formPath}?numeroFormulaire=${selectedForm.numeroFormulaire}&page=Mes%20taches`;

        window.open(url, "_blank");
        closeModal();
        setIsLoading(false);
        break;
      }

      case "Demande AEFO":
        setIsLoading(true);
        sessionStorage.setItem("numeroFormulaire", selectedForm.numeroFormulaire);
        sessionStorage.setItem("userId", selectedForm.userId);
        sessionStorage.setItem("page", "Mes taches");

        window.open("/formAefo", "_blank");
        closeModal();
        setIsLoading(false);

        break;


      case "Demande FEESO":
        setIsLoading(true);
        sessionStorage.setItem("numeroFormulaire", selectedForm.numeroFormulaire);
        sessionStorage.setItem("userId", selectedForm.userId);
        sessionStorage.setItem("page", "Mes taches");

        window.open("/formFeeso", "_blank");
        closeModal();
        setIsLoading(false);
        break;

      case "Demande SCFP":
        setIsLoading(true);
        sessionStorage.setItem("numeroFormulaire", selectedForm.numeroFormulaire);
        sessionStorage.setItem("userId", selectedForm.userId);
        sessionStorage.setItem("page", "Mes taches");

        window.open("/formScfp", "_blank");
        closeModal();
        setIsLoading(false);
        break;



      case "Demande de projet":
        setIsLoading(true);
        window.open(
          `/demandeProjet?numeroFormulaire=${encodeURIComponent(selectedForm.numeroFormulaire)}&page=${encodeURIComponent("Mes taches")}`,
          "_blank",
          "noopener,noreferrer",
        );
        closeModal();
        setIsLoading(false);
      break;

      case "Accès hors pays":
        setIsLoading(true);
        try {
          navigate("/Acceshorspays", {
            state: { selectedForm, page: "Mes taches", traite: traite },
          });
        } catch (error) {
          console.error("Error fetching form data:", error);
        } finally {
          setIsLoading(false); // Stop loading animation
        }
        break;
      default:
        console.warn("Formulaire non reconnu:", nomFormulaire);
    }
  };

  const handleReassignClick = () => {
    setShowReassignPopup(true);
  };

  const handleReassignConfirm = (_id, nomFormulaire) => {
    if (nomUser === "") {
      setErrorReassign("ce champs est requis");
    } else {
      setShowReassignPopup(false);
      // console.log(_id, nomFormulaire, nomUser, idUser, emailUser);
      changeRequest(
        selectedForm._id,
        selectedForm.nomFormulaire,
        "En traitement"
      );
    }
  };

  const handleReassignCancel = () => {
    setShowReassignPopup(false);
    setNomUser("");
    setEmailUser("");
    setIdUser("");
    setErrorReassign("");
  };

  const handleUserSelect = (user) => {
    if (user) {
      setErrorReassign("");
      setNomUser(user.nom);
      setEmailUser(user.email);
      setIdUser(user.userId);
    } else {
      setNomUser("");
      setEmailUser("");
      setIdUser("");
    }

  };



  // Filter requests based on the form number
  // const filteredData = (
  //   Array.isArray(demandeEnAttente) ? demandeEnAttente : []
  // ).filter((form) => {
  //   return (
  //     // form.numeroFormulaire.toLowerCase().includes(filterValue.toLowerCase()) &&
  //     form.nom.toLowerCase().includes(filterNom.toLowerCase()) &&
  //     (statusFilter === "" || form.etatDemande === statusFilter) &&
  //     (nomFormulaire === "" || form.nomFormulaire === nomFormulaire) &&
  //     (acceptedByFilter === "" ||
  //       (form.traitant &&
  //         form.traitant.length > 0 &&
  //         form.traitant[form.traitant.length - 1].userId.includes(user.id))) // Filtre "Moi" ou "Tous le monde"
  //     // isWithinDateRange // Filtre par intervalle de dates
  //   );
  // });

  // const currentData = demandeEnAttente;


  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      order:
        prev.key === columnKey && prev.order === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // const sortedDemandes = [...demandeEnAttente].sort((a, b) => {
  //   if (!sortConfig.key) return 0;

  //   const getValue = (item) => {
  //     if (sortConfig.key === "processingDate") {
  //       return new Date(item.traitant[item.traitant.length - 1]?.dateHeure || 0);
  //     }

  //     if (sortConfig.key === "submissionDate") {
  //       return new Date(item.dateSoumission || 0);
  //     }

  //     return 0;
  //   };

  //   const valA = getValue(a);
  //   const valB = getValue(b);

  //   return sortConfig.order === "asc"
  //     ? valA - valB
  //     : valB - valA;
  // });

  // // Calculer les indices pour la pagination
  // const startIndex = (currentPage - 1) * 25;
  // const endIndex = startIndex + 25;
  // const currentData = sortedDemandes.slice(startIndex, endIndex);

  // ✅ En dehors du composant
const tabs = [
  { label: "À traiter" },
  { label: "Approuvé" },
  { label: "Rejeté" },
  { label: "Soumis" },
];

const getTabBadgeColor = (label) => {
  switch (label) {
    // case "En attente":    return "bg-orange-100 text-orange-700";
    case "À traiter": return "bg-yellow-100 text-yellow-700"; 
    case "Soumis":        return "bg-blue-100 text-blue-700";
    case "Approuvé":      return "bg-green-100 text-green-700";
    case "Rejeté":        return "bg-red-100 text-red-700";
    default:              return "bg-gray-100 text-gray-600";
  }
};
const getActiveTabStyle = (label) => {
  switch (label) {
    case "À traiter":
      return "border-yellow-500 text-slate-900";
    case "Approuvé":
      return "border-green-500 text-slate-900";
    case "Rejeté":
      return "border-red-500 text-slate-900";
    case "Soumis":
      return "border-blue-500 text-slate-900";
    default:
      return "border-slate-400 text-slate-900";
  }
};


// ✅ À l'intérieur du composant
const [activeTab, setActiveTab] = useState("À traiter");
const [itemsPerPage, setItemsPerPage] = useState(15);
// const tabFilteredData = currentData.filter(form => form.etatDemande === activeTab);

  const sortedDemandes = [...demandeEnAttente].sort((a, b) => {
  if (!sortConfig.key) return 0;

  const getValue = (item) => {
    if (sortConfig.key === "processingDate")
      return new Date(item.traitant[item.traitant.length - 1]?.dateHeure || 0);
    if (sortConfig.key === "submissionDate")
      return new Date(item.dateSoumission || 0);
    return 0;
  };

  const valA = getValue(a);
  const valB = getValue(b);
  return sortConfig.order === "asc" ? valA - valB : valB - valA;
});

// Filtrer par onglet AVANT la pagination
const tabSortedData =
  activeTab === "À traiter"
    ? sortedDemandes.filter(
        (f) =>
          f.etatDemande === "En attente" ||
          f.etatDemande === "En traitement"
      )
    : sortedDemandes.filter(
        (f) => f.etatDemande === activeTab
      );

// Paginer sur les données filtrées
const startIndex = (currentPage - 1) * itemsPerPage;
const endIndex = startIndex + itemsPerPage;
const currentData = tabSortedData.slice(startIndex, endIndex);
const firstVisibleItem = startIndex + 1;
const lastVisibleItem = Math.min(endIndex, tabSortedData.length);

useEffect(() => {
  setTotalPages(Math.ceil(tabSortedData.length / itemsPerPage));
}, [tabSortedData, itemsPerPage, setTotalPages]);

const getVisiblePages = () => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "end-ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [
      1,
      "start-ellipsis",
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "start-ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "end-ellipsis",
    totalPages,
  ];
};

  // Fonction pour changer la page
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const changeRequest = async (
    _id,
    nomFormulaire,
    etatDemande,
    courrielDemandeur,
    numeroFormulaire,
    garderPanneauOuvert = false
  ) => {
    // console.log("Accept request : ", _id);
    // console.log("here is the email",courrielDemandeur)
    if (
      (etatDemande === "Rejeté" && !rejectComment.trim()) ||
      (etatDemande === "Rejeté" && !showComment.trim())
    ) {
      setErrorMessage("champs requis");
      return;
    } else {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/modify-etatdemande/${_id}`,
          {
            method: "PUT", // or 'POST' depending on your backend setup
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              etatDemande,
              // etatDemande: "Approuvé",
              nomFormulaire: nomFormulaire,
              userId: idUser ? idUser : user.id,
              nom: nomUser ? nomUser : user.displayName,
              email: emailUser ? emailUser : user.mail,
              commentaire: nomUser ? `demande réassignée` : rejectComment,
              commentaireVisible: showComment,
              courrielDemandeur,
              numeroFormulaire,
            }), // Update this value as needed
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || result.message);
        }

        // ✅ Emit to server that a request has changed
        if (socket) {
          socket.emit("requestUpdated");
        }

        if (garderPanneauOuvert) {
          setSelectedForm((formulaireActuel) => {
            if (!formulaireActuel) return formulaireActuel;

            const nouveauTraitant = {
              userId: user.id,
              nom: user.displayName,
              email: user.mail,
              commentaire: rejectComment,
              etatDemande,
              dateHeure: new Date().toISOString(),
            };

            return {
              ...formulaireActuel,
              etatDemande,
              traitant:
                nomFormulaire === "Réclamation mensuelle de dépenses"
                  ? formulaireActuel.traitant
                  : [...(formulaireActuel.traitant || []), nouveauTraitant],
            };
          });
        } else {
          setSelectedForm(null);
        }
        setshowReject(false);
        setRejectComment("");
        setShowComment("");
        if (etatDemande === "En traitement" && nomUser) {
          setMessage({
            message: "La demande a été réassignée",
            color: "text-green-600",
          });
        } else if (etatDemande === "Approuvé") {
          setMessage({
            message: "La demande a été Approuvée",
            color: "text-green-600",
          });
        } else if (etatDemande === "Rejeté") {
          setMessage({
            message: "La demande a été Rejetée",
            color: "text-red-600",
          });
        }
        handleReassignCancel();
        if (!garderPanneauOuvert) {
          setIsAccepted(true);
          setTimeout(() => {
            setIsAccepted(false);
          }, 3000);
        }
      } catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    }
    }
  };

  
const handleCloturerClick = async (_id) => {
     try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/cloturerDemandeProjet/${_id}`,
          {
            method: "PUT", // or 'POST' depending on your backend setup
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message);
      }
        // ✅ Emit to server that a request has changed
        if (socket) {
          socket.emit("requestUpdated");
        }

        setSelectedForm(null);
        
        setMessage({
          message: "La demande a été cloturée",
          color: "text-blue-600",
        });
        
        setIsAccepted(true);
        setTimeout(() => {
          setIsAccepted(false);
        }, 3000);
      } catch (error) {
      console.error(error);
      showError(error); // ou setError(error.message)
    }
  };

  // Fonction pour ouvrir le modal et afficher les détails de l'élément sélectionné
  const handleRowClick = (form) => {
    setDerniereDemandeSelectionnee(form.numeroFormulaire || form._id);
    setSelectedForm(form);
  };

  const handleRowKeyDown = (event, form) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRowClick(form);
    }
  };

  // Fonction pour fermer le modal
  const closeModal = () => {
    setSelectedForm(null); // Réinitialiser l'élément sélectionné
    setshowReject(false);
    setshowConfirm(false);
    setRejectComment("");
    setShowComment("");
    setErrorMessage("");
    setErrorShowComment("");
  };

  const panneauOuvert = Boolean(selectedForm);

  useEffect(() => {
    if (!panneauOuvert) return undefined;

    const scrollY = window.scrollY;
    const previousBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      Object.assign(document.body.style, previousBodyStyles);
      window.scrollTo(0, scrollY);
    };
  }, [panneauOuvert]);

  useEffect(() => {
    if (!selectedForm) return undefined;

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (showReassignPopup) {
        setShowReassignPopup(false);
        setNomUser("");
        setEmailUser("");
        setIdUser("");
        setErrorReassign("");
        return;
      }

      if (showReject) {
        setshowReject(false);
        return;
      }

      if (showConfirm) {
        setshowConfirm(false);
        return;
      }

      setSelectedForm(null);
      setRejectComment("");
      setShowComment("");
      setErrorMessage("");
      setErrorShowComment("");
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [selectedForm, showConfirm, showReject, showReassignPopup]);
  const closePopup = () => {
    setShowPopup(false);
  };

  const Field = ({ label, value, isPassword, showPassword, setShowPassword }) => {
  return (
    <div>
      <div className="block text-sm font-medium text-blue-900 mb-1">{label}</div>

      <div className="relative mt-1 w-full p-3 border border-gray-200 rounded-xl
                   bg-white shadow-sm
                   break-words whitespace-pre-wrap">
        {isPassword ? (
          <>
            <span>
              {showPassword ? value : "••••••••••"}
            </span>

            {/* <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="ml-2 text-gray-500 hover:text-gray-700"
              aria-label={showPassword ? "Masquer" : "Afficher"}
            >
              {showPassword ? "🙈" : "👁️"}
            </button> */}

            <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                      aria-label={
                        showPassword
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {showPassword ? (
                        // Eye-off icon
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9.27-3.11-11-7
               1.02-2.32 2.82-4.27 5.125-5.675M6.1 6.1A9.969 9.969 0 0112 5
               c5 0 9.27 3.11 11 7a10.97 10.97 0 01-4.145 5.355M15 12a3 3 0 01-3 3
               m0-6a3 3 0 013 3m6 6L4 4.5"
                          />
                        </svg>
                      ) : (
                        // Eye icon
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5
               c4.478 0 8.268 2.943 9.542 7
               -1.274 4.057-5.064 7-9.542 7
               -4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
          </>
        ) : (
          value
        )}
      </div>
    </div>
  );
};


const formatDateTime = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};
const formatPhone = (phone) => {
  if (!phone || phone.length < 10) return phone;
  return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 10)}`;
};

const renderGrid = (fields) => {
  const visibleFields = fields.filter(
    (f) => f.value !== undefined && f.value !== null && f.value !== ""
  );

  if (visibleFields.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
      {visibleFields.map((f, i) => (
        <Field
          key={i}
          label={f.label}
          value={f.value}
          isPassword={f.isPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
        />
      ))}
    </div>
  );
};

  const renderFormDetails = (form) => (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-xl shadow-sm border border-gray-200 space-y-2">
        {/* === Informations générales === */}
      {renderGrid([
        { label: "Formulaire", value: form.nomFormulaire },
        { label: "Numéro de formulaire", value: form.numeroFormulaire },
        { label: "Nom", value: form.nom },
        { label: "Courriel", value: form.courriel },
        { label: "Poste", value: form.posteOccupe },
        { label: "Matricule centrale", value: form.matricule },
        { label: "Date de soumission", value: formatDateTime(form.dateSoumission) },
        { label: "État de la demande", value: form.etatDemande },
      ])}

        {/* === Membre du personnel === */}
      {renderGrid([
        { label: "Nom du membre du personnel", value: form.nomMembre },
        { label: "Prénom du membre du personnel", value: form.prenomMembre },
        { label: "Poste du membre du personnel", value: form.posteMembre },
        { label: "Lieu de travail", value: form.lieuTravail },
      ])}

     {/* === Déclaration === */}
      {renderGrid([
         {
    label: "Type de la demande",
    value:
      form?.typeDemande
        ? form.typeDemande === "rapportPolice"
          ? "Rapport de police"
          : "Déclaration annuelle des antécédents criminels"
        : null,
  },
        {
          label: "Coupable",
          value: form.coupable,
        },
        {
    label: "Type de document",
    value:
      form?.typeDocument
        ? form.typeDocument === "RecuDemande"
          ? "Accusé de réception de la demande de certificat de police"
          : "Certificat de police"
        : null,
  },
        {
          label: "Dat de document",
          value: form.dateDocumentPolice,
        },   
         {
          label: "Mot de passe",
          value: form.motdepasseFile,
          isPassword: true,
        },
        {
          label: "Déclaration exacte et complète",
          value: form.declarationExacteEtComplete,
        }
      ])}

       
        {form.adressePostalResidentielle === "non" && (
          <p className="mt-3 block text-lg font-medium text-blue-900 pt-5">
            <strong>Adresse Résidentielle </strong>
          </p>
        )}

        {/* === Adresse résidentielle === */}
      {renderGrid([
        { label: "Adresse", value: form.adresse },
        { label: "Appartement", value: form.numeroappartement },
        { label: "Ville", value: form.ville },
        { label: "Province", value: form.province },
        { label: "Code postal", value: form.codePostal },
      ])}

        {form.adressePostalResidentielle === "non" && (
          <p className="mt-3 block text-lg font-medium text-blue-900 pt-5">
            <strong>Adresse Postale </strong>
          </p>
        )}

        {form.adressePostalResidentielle === "non" && (
        <>

          {renderGrid([
            { label: "Boîte postale", value: form.boitePostale },
            { label: "Appartement / unité", value: form.appartementPostale },
            { label: "Ville", value: form.villePostale },
            { label: "Province", value: form.provincePostale },
            { label: "Code postal", value: form.codePostalPostale },
          ])}
        </>
      )}


        {/* === Coordonnées === */}
      {renderGrid([
        {
          label: "Numéro de téléphone principal",
          value: formatPhone(form.telephone),
        },
        {
          label: "Autre numéro de téléphone",
          value: formatPhone(form.telephoneCellulaire),
        },
        { label: "Date d’entrée en vigueur", value: form.dateEntreeEnVigueur },
      ])}

        {/* === Dates & superviseur === */}
      {renderGrid([
        { label: "Date de début", value: form.dateDebut },
        { label: "Date de fin", value: form.dateFin },
        {
          label: "Courriel du superviseur",
          value: form.courrielSuperviseur,
        },
      ])}

        {/* === Commentaires === */}
      {form.commentairesDemandeur && (
        <div className="pt-6">
          <label className="block text-sm font-medium text-blue-900 mb-1">
            Commentaires du demandeur :
          </label>
          <div className="p-3 border border-gray-200 rounded-xl bg-white whitespace-pre-wrap break-words">
            {form.commentairesDemandeur}
          </div>
        </div>
      )}


        {/* === Changement de nom === */}
      {renderGrid([
        { label: "Nouveau nom", value: form.nouveauNom },
        { label: "Nouveau prénom", value: form.nouveauPrenom },
        { label: "Deuxième prénom", value: form.deuxiemePrenom },
      ])}

        {/* === Banque === */}
      {renderGrid([
        { label: "Institution", value: form.institution },
        { label: "Banque", value: form.banque },
        { label: "Numéro de transit", value: form.numeroTransit },
        { label: "Numéro de compte", value: form.numeroCompte },
      ])}

      {/* === Réclamation === */}
      {renderGrid([
        {
          label: "Période de la réclamation",
          value: form.periodReclamation
            ? new Intl.DateTimeFormat("fr-CA", {
                year: "numeric",
                month: "long",
                timeZone: "UTC",
              }).format(new Date(form.periodReclamation))
            : null,
        },
        {
          label: "Document justificatif",
          value: form.documentJustificatif,
        },
      ])}

 {/* Bouton pour voir le fichier */}
        {(form.nomFormulaire==="Changement information bancaire"||
        form.nomFormulaire==="Changement de nom"||
        (
        form.nomFormulaire==="Antécédents criminels"
        &&
        (form.typeDemande==="rapportPolice"
        ||
            (form.typeDemande==="declarationAnnuelle"
            &&
            form.coupable==="oui"
          )
            )
        )

        )
         && (
        <div className="mt-4 w-3/4 flex items-center">
  <button
    onClick={() => fetchUploadedFile(form.numeroFormulaire)}
    className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
  >
    {loadingFile ? (
      <span className="animate-pulse">Chargement...</span>
    ) : (
      <>
        📄 <span>Voir le document</span>
      </>
    )}
  </button>
</div>
        )}
{showPopup && (
  <>
    {/* BACKDROP */}
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
      onClick={closePopup}
    ></div>

    {/* MODAL */}
    <div className="fixed w-[65%] max-w-5xl h-[85%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">

      {/* HEADER */}
      <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            Aperçu du fichier
          </h3>
          <p className="text-xs text-gray-500">
            Visualisation rapide du document
          </p>
        </div>

        <button
          onClick={closePopup}
          className="text-gray-400 hover:text-gray-700 text-xl transition"
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="flex-1 overflow-auto p-6 flex justify-center items-center bg-gray-50">

        {/* LOADING */}
        {!uploadedFile && (
          <div className="flex flex-col items-center gap-3 text-gray-500">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p>Chargement du fichier...</p>
          </div>
        )}

        {/* IMAGE */}
        {uploadedFile?.contentType?.startsWith("image/") && (
          <div className="space-y-4 text-center">
            <div className="mt-10 flex items-center justify-center gap-3 text-sm text-white font-medium bg-green-700 backdrop-blur px-4 py-2 rounded-full shadow-sm">
              <span>Télécharger :</span>
              <a
                href={`data:${uploadedFile.contentType};base64,${uploadedFile.data}`}
                download
                className="p-2 rounded-full bg-white shadow hover:bg-gray-100 transition"
              >
                <Download className="w-4 h-4 text-gray-700" />
              </a>
            </div>

            <SlideshowLightbox>
              <img
                src={`data:${uploadedFile.contentType};base64,${uploadedFile.data}`}
                alt="Aperçu"
                className="max-h-[500px] rounded-xl shadow-md border cursor-zoom-in hover:scale-[1.02] transition"
              />
            </SlideshowLightbox>
          </div>
        )}

        {/* PDF */}
        {uploadedFile?.contentType === "application/pdf" && pdfUrl && (
  <iframe
    src={pdfUrl}
    title="PDF preview"
    className="w-full h-[90%] border mt-4 rounded-lg"
  />
)}
        {/* {uploadedFile?.contentType === "application/pdf" && (
          <iframe
            src={`data:application/pdf;base64,${uploadedFile.data}`}
            title="PDF preview"
            className="w-full h-full rounded-lg border shadow-sm"
          />
        )} */}
      </div>
    </div>
  </>
)}

        {/* {form.uploadedFile && (
          <div className="mt-3 w-3/4 flex items-center space-x-4 pt-5">
            <p
              className="border rounded-lg px-4 py-2 w-[50%] bg-blue-900 text-white cursor-pointer inline-block hover:bg-blue-700 transition duration-200"
              onClick={() => setShowPopup(true)}
            >
              Voir le document : {form.uploadedFile.filename}
            </p>
          </div>
        )}
        {showPopup && form.uploadedFile?.data && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={closePopup}
            ></div>
            <div className="fixed w-[60%] h-[80%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-6 bg-white border shadow-lg rounded-lg z-50">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Aperçu du fichier
                  </h3>
                  {form.uploadedFile.contentType?.startsWith("image/") && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        Télécharger :
                        <a
                          href={`data:${form.uploadedFile.contentType};base64,${form.uploadedFile.data}`}
                          download
                          className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                          title="Télécharger l'image"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </a>
                      </p>
                      <p className="text-sm text-gray-500">
                        Cliquez sur l’image pour l’agrandir
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onClick={closePopup}
                  className="absolute top-2 right-4 ml-4 text-gray-500 hover:text-gray-700 font-bold text-xl transition"
                  aria-label="Fermer"
                >
                  X
                </button>
              </div>

              
              {form.uploadedFile.contentType?.startsWith("image/") && (
                <SlideshowLightbox className="mx-auto">
                  <img
                    src={`data:${form.uploadedFile.contentType};base64,${form.uploadedFile.data}`}
                    alt="Aperçu"
                    className="max-w-full max-h-[500px] rounded-lg border cursor-zoom-in"
                  />
                </SlideshowLightbox>
              )}

              
              {form.uploadedFile.contentType === "application/pdf" && (
                <iframe
                  src={`data:application/pdf;base64,${form.uploadedFile.data}`}
                  title="PDF preview"
                  className="w-[100%] h-[90%] border mt-4"
                />
              )}
            </div>
          </>
        )} */}

        {form.dispositions && form.dispositions.length > 0 && (
          <div className="mt-8 pt-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Dispositions :
            </h3>

            <ResponsiveDetailsTable
              rows={form.dispositions}
              getRowKey={(disposition, index) => disposition._id || index}
              columns={[
                {
                  key: "infraction",
                  label: "Infraction",
                  render: (disposition) => disposition.infractions,
                },
                {
                  key: "date",
                  label: "Date",
                  render: (disposition) => disposition.date,
                },
                {
                  key: "tribunal",
                  label: "Tribunal",
                  render: (disposition) => disposition.tribunal,
                },
                {
                  key: "commentaire",
                  label: "Commentaire",
                  render: (disposition) => disposition.commentaire,
                },
              ]}
            />

          </div>
        )}

        {form.commentaire && (
          <p className="mt-4 text-lg text-gray-700">
            <strong
              className={
                form.etatDemande === "Approuvé"
                  ? "text-green-600"
                  : form.etatDemande === "Rejeté"
                    ? "text-red-600"
                    : "text-gray-600"
              }
            >
              Note du traiteur :
            </strong>{" "}
            {form.commentaire}
          </p>
        )}

        {form.traitant?.length > 0 && (
          <div className="mt-8 pt-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Traitants :
            </h3>
            <ResponsiveDetailsTable
              rows={form.traitant.filter((traitant, index, traitants) => {
                if (
                  traitant.etatDemande === "Enregistrer" &&
                  traitants[index + 1]?.etatDemande === "En traitement"
                ) {
                  return false;
                }

                if (
                  traitant.etatDemande === "En traitement" &&
                  traitants[index - 1]?.etatDemande === "Enregistrer"
                ) {
                  return false;
                }

                return true;
              })}
              getRowKey={(traitant, index) => traitant._id || index}
              columns={[
                {
                  key: "nom",
                  label: "Nom",
                  render: (traitant) => traitant.nom,
                },
                {
                  key: "email",
                  label: "Email",
                  render: (traitant) => traitant.email,
                },
                {
                  key: "etat",
                  label: "État",
                  render: (traitant) => traitant.etatDemande,
                },
                {
                  key: "date",
                  label: "Date et heure",
                  render: (traitant) => formatDate(traitant.dateHeure),
                },
                ...(form.nomFormulaire !== "Réclamation mensuelle de dépenses"
                  ? [
                      {
                        key: "notes",
                        label: "Notes",
                        render: (traitant) => traitant.commentaire,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );

  function formatDate(dateInput) {
    try {
      if (!dateInput) return "Date invalide";

      // Si c'est déjà un objet Date valide
      if (dateInput instanceof Date && !isNaN(dateInput)) {
        return dateInput.toLocaleString("fr-CA", {
          dateStyle: "short",
          timeStyle: "short",
        });
      }

      // Sinon, on tente avec le constructeur Date
      const parsed = new Date(dateInput);

      // Si le constructeur ne fonctionne pas, on essaie avec Date.parse()
      if (isNaN(parsed)) {
        const timestamp = Date.parse(dateInput);
        if (!isNaN(timestamp)) {
          return new Date(timestamp).toLocaleString("fr-CA", {
            dateStyle: "short",
            timeStyle: "short",
          });
        } else {
          return "Date invalide";
        }
      }

      return parsed.toLocaleString("fr-CA", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch (e) {
      return "Date invalide";
    }
  }
  const formatOptionalDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };
  const getStatusDotColor = (etat) => {
    switch (etat) {
      case "En attente":
        return "bg-orange-500";
      case "Soumis":
      return "bg-blue-500";
      case "En traitement":
        return "bg-yellow-500";
      case "À traiter":
        return "bg-yellow-500";
      case "Rejeté":
        return "bg-red-500";
      case "Approuvé":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  // const handleStatusChange = (event) => {
  //   setStatusFilter(event.target.value);
  // };
  const handleLieuChange = (event) => {
    setLieuFilter(event.target.value);
  };
  
  const handleAssigneChange = (event) => {
    setAssigneFilter(event.target.value);
  };
  const handleNomFormulaire = (event) => {
    const formType = event.target.value;
    setNomFormulaire(formType);
    if (formType !== "Réclamation mensuelle de dépenses") {
      setPeriodeReclamation("");
    }
  };

  const handlePeriodeReclamationChange = (value) => {
    setPeriodeReclamation(value);
    if (value) {
      setNomFormulaire("Réclamation mensuelle de dépenses");
      setStartDate("");
      setEndDate("");
    }
    setCurrentPage(1);
  };
  // Update filter value based on input
  const handleFilterChange = (event) => {
    setFilterValue(event.target.value);
  };

  // Update filter value based on input
  const handleFilterNom = (event) => {
    setFilterNom(event.target.value);
  };
  // const handleAcceptedByChange = (event) => {
  //   setAcceptedByFilter(event.target.value);
  //   setCurrentPage(1); // Réinitialiser à la première page
  // };

  const handleResetFilters = () => {
    setFilterValue(""); // Réinitialise le champ de numéro de formulaire
    setFilterNom("");
    // setStatusFilter(""); // Réinitialise le sélecteur de statut
    setLieuFilter("");
    setAssigneFilter("");
    setAcceptedByFilter(""); // Réinitialise le filtre "Accepté par"
    setStartDate(""); // Réinitialise la date "De"
    setEndDate(""); // Réinitialise la date "À"
    setNomFormulaire("");
    setPeriodeReclamation("");
    setCurrentPage(1);
  };



const [showFilters, setShowFilters] = useState(false);
const activeFiltersCount = [
  filterValue,
  filterNom,
  nomFormulaire,
  periodeReclamation,
  lieuFilter,
  assigneFilter,
  startDate,
  endDate,
].filter(Boolean).length;

const overridesDefaultDateRange = Boolean(
  periodeReclamation || filterValue || filterNom || startDate || endDate,
);

const resultScopeMessage = periodeReclamation
  ? "Résultats pour le mois des dépenses sélectionné."
  : filterValue
    ? "Résultats correspondant au numéro recherché."
    : filterNom
      ? "Résultats correspondant au nom recherché."
      : startDate || endDate
        ? "Résultats selon la période de soumission sélectionnée."
        : "Les demandes affichées correspondent aux 30 derniers jours. Si vous souhaitez voir plus de demandes, utilisez les filtres.";


  return (
    <div>
      {demandeEnAttente && (
        <div>
          {/* <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 transition-all duration-300">


  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

    
    <div className="flex flex-col">
      <label htmlFor="filterValue" className="mb-1 text-md font-semibold text-gray-700">
        Numéro du formulaire
      </label>
      <input
        type="text"
        id="filterValue"
        placeholder="Entrez le numéro"
        value={filterValue}
        onChange={(e) => handleFilterChange(e)}
        className={`p-3 border rounded-xl shadow-sm focus:outline-none transition-all duration-200
          ${messageErreur ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'}`}
      />
      {messageErreur && (
        <p className="mt-1 text-xs text-red-600 break-words">
          {messageErreur}
        </p>
      )}
    </div>

 
    <div className="flex flex-col">
      <label htmlFor="filterNom" className="mb-1 text-md font-semibold text-gray-700">
        Nom de la personne
      </label>
      <input
        type="text"
        id="filterNom"
        placeholder="Entrez le nom"
        value={filterNom}
        onChange={(e) => handleFilterNom(e)}
        className={`p-3 border rounded-xl shadow-sm focus:outline-none transition-all duration-200
          ${messageErreurNom ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'}`}
      />
      {messageErreurNom && (
        <p className="mt-1 text-xs text-red-600 break-words">
          {messageErreurNom}
        </p>
      )}
    </div>

   
    <div className="flex flex-col">
      <label htmlFor="statusFilter" className="mb-1 text-md font-semibold text-gray-700">
        Statut
      </label>
      <select
        id="statusFilter"
        value={statusFilter}
        onChange={(e) => handleStatusChange(e)}
        className="p-3 border border-gray-300 rounded-xl shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
      >
        <option value="">Tous les statuts</option>
        <option value="En attente">En attente</option>
        <option value="En traitement">En traitement</option>
        <option value="Rejeté">Rejeté</option>
        <option value="Approuvé">Approuvé</option>
        <option value="Soumis">Soumis</option>
      </select>
    </div>

    
    <div className="flex flex-col">
      <label htmlFor="nomFormulaire" className="mb-1 text-md font-semibold text-gray-700">
        Formulaire
      </label>
      <select
        id="nomFormulaire"
        value={nomFormulaire}
        onChange={(e) => handleNomFormulaire(e)}
        className="p-3 border border-gray-300 rounded-xl shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
      >
        <option value="">Tous les formulaires</option>
        {canAccessManagedForm("AccesHP") && <option value="Accès hors pays">Accès hors pays</option>}
        {canAccessManagedForm("ReseauInfo") && <option value="Accès au compte informatique">Accès au compte informatique</option>}
        {canAccessManagedForm("AchatEI") && <option value="Achat équipement informatique">Achat équipement informatique</option>}
        {canAccessManagedForm("AntecedentCriminel") && <option value="Antécédents criminels">Antécédents criminels</option>}
        {canAccessManagedForm("Adresse") && <option value="Changement adresse">Changement d'adresse</option>}
        {canAccessManagedForm("Nom") && <option value="Changement nom">Changement nom</option>}
        {canAccessManagedForm("InfoBancaire") && <option value="Changement information bancaire">Changement information bancaire</option>}
        <option value="Demande AEFO">Demande AEFO</option>
        <option value="Demande FEESO">Demande FEESO</option>
        <option value="Demande SCFP">Demande SCFP</option>
        {canAccessManagedForm("ReclamationDepense") && <option value="Réclamation mensuelle de dépenses">Réclamation mensuelle de dépenses</option>}
      </select>
    </div>

   
    {(checkGroupSeancePlacement || checkGroupAdmin) && (
      <div className="flex flex-col">
        <label htmlFor="lieuFilter" className="mb-1 text-md font-semibold text-gray-700">
          Lieu de travail
        </label>
        <select
          id="lieuFilter"
          value={lieuFilter}
          onChange={(e) => handleLieuChange(e)}
          className="p-3 border border-gray-300 rounded-xl shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
          <option value="">Tous les lieux</option>
          {excelDataEcole.map((row, i) => (
            <option key={`${row.École}-${i}`} value={row.École}>
              {row.École}
            </option>
          ))}
        </select>
      </div>
    )}

   
    {(checkGroupSeancePlacement || checkGroupAdmin) && (
      <div className="flex flex-col">
        <label htmlFor="assigneFilter" className="mb-1 text-md font-semibold text-gray-700">
          Assigné à
        </label>
        <select
          id="assigneFilter"
          value={assigneFilter}
          onChange={(e) => handleAssigneChange(e)}
          className="p-3 border border-gray-300 rounded-xl shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
          <option value="">Tous</option>
          {listSpUsers.map((row) => (
            <option key={row._id} value={row.userId}>
              {row.nom}
            </option>
          ))}
        </select>
      </div>
    )}

   
    <div className="flex flex-col">
      <label htmlFor="startDate" className="mb-1 text-md font-semibold text-gray-700">
        De
      </label>
      <input
        type="date"
        id="startDate"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        className={`p-3 border rounded-xl shadow-sm focus:outline-none transition-all duration-200
          ${messageErreurDate ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'}`}
      />
      {messageErreurDate && (
        <p className="mt-1 text-xs text-red-600 break-words">
          {messageErreurDate}
        </p>
      )}
    </div>

    <div className="flex flex-col">
      <label htmlFor="endDate" className="mb-1 text-md font-semibold text-gray-700">
        À
      </label>
      <input
        type="date"
        id="endDate"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        className="p-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
      />
    </div>

  </div>

  
  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
    <button
      onClick={() => {
        setAuthLoading(true);
        setShouldFetch(true);
      }}
      className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-semibold px-3 py-3 rounded-xl shadow-md transition-all duration-200"
    >
      Rechercher
    </button>

    <button
      onClick={(e) => {
        setAuthLoading(true);
        handleResetFilters(e);
        setShouldFetch(true);
      }}
      className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white font-semibold px-3 py-3 rounded-xl shadow-md transition-all duration-200"
    >
      Réinitialiser
    </button>
  </div>
</div> */}

<div className="mb-4 flex items-center gap-4">
  <button
    type="button"
    onClick={() => setShowFilters((prev) => !prev)}
    className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold shadow-sm transition-all duration-200 hover:shadow-md ${
      showFilters
        ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
        : "border-blue-400 bg-white text-blue-700"
    }`}
    aria-expanded={showFilters}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`h-5 w-5 ${showFilters ? "text-white" : "text-slate-500"}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
      />
    </svg>
    <span>Filtres</span>
    {activeFiltersCount > 0 && (
      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-xs font-bold text-blue-700">
        {activeFiltersCount}
      </span>
    )}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`h-4 w-4 transition-transform duration-200 ${
        showFilters ? "rotate-180 text-white" : "rotate-0 text-slate-400"
      }`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </button>

  <span className="hidden text-sm text-slate-500 md:inline">
    Filtrer les demandes rapidement
  </span>
</div>
{/* Panel filtres */}
{showFilters && (
  <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
    <div className="p-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">

        {/* Numéro du formulaire */}
        <div className="flex flex-col">
          <label htmlFor="filterValue" className="mb-1.5 text-sm font-semibold text-slate-700">
            Numéro du formulaire
          </label>
          <input
            type="text"
            id="filterValue"
            placeholder="Entrez le numéro"
            value={filterValue}
            onChange={(e) => handleFilterChange(e)}
            className={`h-12 rounded-xl border bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200
              ${messageErreur
                ? "border-red-300 focus:ring-4 focus:ring-red-100"
                : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"}`}
          />
          {messageErreur && (
            <p className="mt-1.5 text-sm text-red-500">{messageErreur}</p>
          )}
        </div>

        {/* Nom */}
        <div className="flex flex-col">
          <label htmlFor="filterNom" className="mb-1.5 text-sm font-semibold text-slate-700">
            Nom de la personne
          </label>
          <input
            type="text"
            id="filterNom"
            placeholder="Entrez le nom"
            value={filterNom}
            onChange={(e) => handleFilterNom(e)}
            className={`h-12 rounded-xl border bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200
              ${messageErreurNom
                ? "border-red-300 focus:ring-4 focus:ring-red-100"
                : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"}`}
          />
          {messageErreurNom && (
            <p className="mt-1.5 text-sm text-red-500">{messageErreurNom}</p>
          )}
        </div>

        {/* Formulaire */}
        <div className="flex flex-col">
          <label htmlFor="nomFormulaire" className="mb-1.5 text-sm font-semibold text-slate-700">
            Formulaire
          </label>
          <select
            id="nomFormulaire"
            value={nomFormulaire}
            onChange={(e) => handleNomFormulaire(e)}
            className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
          >
            <option value="">Tous les formulaires</option>
            {canAccessManagedForm("AccesHP") && <option value="Accès hors pays">Accès hors pays</option>}
            {canAccessManagedForm("ReseauInfo") && <option value="Accès au compte informatique">Accès au compte informatique</option>}
            {canAccessManagedForm("AchatEI") && <option value="Achat équipement informatique">Achat équipement informatique</option>}
            {canAccessManagedForm("AntecedentCriminel") && <option value="Antécédents criminels">Antécédents criminels</option>}
            {canAccessManagedForm("Adresse") && <option value="Changement adresse">Changement d'adresse</option>}
            {canAccessManagedForm("Nom") && <option value="Changement de nom">Changement de nom</option>}
            {canAccessManagedForm("InfoBancaire") && <option value="Changement information bancaire">Changement information bancaire</option>}
            {(checkGroupSeancePlacement || checkGroupAdmin) && (
              <>
                <option value="Demande AEFO">Demande AEFO</option>
                <option value="Demande FEESO">Demande FEESO</option>
                <option value="Demande SCFP">Demande SCFP</option>
              </>
            )}
            {checkGroupDemandeProjet === true && canAccessManagedForm("DemandeProjet") && (
              <option value="Demande de projet">Demande de projet</option>
            )}
        {canAccessManagedForm("EntenteTravailHybrid") && <option value="Entente de travail en mode hybride">Entente de travail en mode hybride</option>}
        {canAccessManagedForm("ReclamationDepense") && <option value="Réclamation mensuelle de dépenses">Réclamation mensuelle de dépenses</option>}
          </select>
        </div>

        {/* Mois des dépenses */}
        <div className="flex flex-col">
          <label htmlFor="periodeReclamationTaches" className="mb-1.5 text-sm font-semibold text-slate-700">
            Mois des dépenses
          </label>
          <ModernMonthPicker
            value={periodeReclamation}
            onChange={handlePeriodeReclamationChange}
            max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`}
            ariaLabel="Choisir le mois des dépenses"
          />
        </div>

        {/* Lieu */}
        {(checkGroupSeancePlacement || checkGroupAdmin) && (
          <div className="flex flex-col">
            <label htmlFor="lieuFilter" className="mb-1.5 text-sm font-semibold text-slate-700">
              Lieu de travail
            </label>
            <select
              id="lieuFilter"
              value={lieuFilter}
              onChange={(e) => handleLieuChange(e)}
              className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            >
              <option value="">Tous les lieux</option>
              {excelDataEcole.map((row, i) => (
                <option key={`${row.École}-${i}`} value={row.École}>
                  {row.École}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Assigné à */}
        {(checkGroupSeancePlacement || checkGroupAdmin) && (
          <div className="flex flex-col">
            <label htmlFor="assigneFilter" className="mb-1.5 text-sm font-semibold text-slate-700">
              Assigné à
            </label>
            <select
              id="assigneFilter"
              value={assigneFilter}
              onChange={(e) => handleAssigneChange(e)}
              className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            >
              <option value="">Tous</option>
              {listSpUsers.map((row) => (
                <option key={row._id} value={row.userId}>
                  {row.nom}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Période de soumission */}
        {periodeReclamation ? (
          <div className="sm:col-span-2 xl:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Période de soumission
            </label>
            <div className="flex h-12 items-center gap-2 px-1 text-sm text-slate-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 shrink-0 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Toutes les dates de soumission sont incluses.</span>
            </div>
          </div>
        ) : (
          <div className="sm:col-span-2 xl:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Période de soumission
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`h-12 rounded-xl border bg-white px-3 text-sm text-slate-800 outline-none transition-all duration-200
                    ${messageErreurDate
                      ? "border-red-300 focus:ring-4 focus:ring-red-100"
                      : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"}`}
                />
                {messageErreurDate && (
                  <p className="mt-1.5 text-sm text-red-500">{messageErreurDate}</p>
                )}
              </div>

              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-start">
        <div className="flex items-center gap-3 justify-start">
          <button
            onClick={(e) => {
              setAuthLoading(true);
              handleResetFilters(e);
              setShouldFetch(true);
            }}
            className="inline-flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-blue-600 transition-colors duration-200 hover:bg-blue-50 hover:text-blue-800"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h5M20 20v-5h-5M5.64 18.36A8 8 0 0018.36 5.64M18.36 5.64A8 8 0 005.64 18.36"
              />
            </svg>
            <span>Réinitialiser</span>
          </button>

          <button
            onClick={() => {
              setAuthLoading(true);
              setShouldFetch(true);
            }}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-blue-700"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            Rechercher
          </button>
        </div>
      </div>
    </div>
  </div>
)}

          {/* <h2 className="text-2xl font-bold mb-2 text-gray-800 mt-8">
            Liste des Demandes
          </h2>
          <div className="flex items-start gap-3 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
           
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
              />
            </svg>

            <p className="text-gray-700 text-base">
              Les données affichées dans le tableau correspondent aux{" "}
              <strong>deux derniers mois</strong>. Pour consulter des données
              plus anciennes, veuillez utiliser le filtrage et sélectionner
              l’intervalle de dates souhaité.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed">
              <thead className="bg-gray-100 border-b">
                <tr className="text-gray-700 font-medium">

                  <th className="px-4 py-3 text-left">Nom du formulaire</th>
                  <th className="px-4 py-3 text-left">Numéro de formulaire</th>
                  <th className="px-4 py-3 text-left">Demandeur</th>

                  
                  <th
                    onClick={() => handleSort("submissionDate")}
                    className={`px-4 py-3 text-left cursor-pointer transition-colors
                   hover:text-blue-600
                         ${sortConfig.key === "submissionDate" ? "text-blue-600" : ""}
                            `}
                  >
                    Date de soumission
                    {sortConfig.key === "submissionDate" && (
                      <span className="ml-2 text-sm">
                        {sortConfig.order === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>

                  <th className="px-4 py-3 text-left">Statut</th>

                  {(checkGroupRecMensuelle || checkGroupAdmin) && (
                    <th
                      onClick={() => handleSort("processingDate")}
                      className={`px-4 py-3 text-left cursor-pointer transition-colors
              hover:text-blue-600
              ${sortConfig.key === "processingDate" ? "text-blue-600" : ""}
            `}
                    >
                      Date d’attribution
                      {sortConfig.key === "processingDate" && (
                        <span className="ml-2 text-sm">
                          {sortConfig.order === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                  )}

                  {(checkGroupSeancePlacement || checkGroupAdmin) && (
                    <th className="px-4 py-3 text-left">Lieu de travail</th>
                  )}



                  <th className="px-4 py-3 text-left">Assignée</th>

                </tr>
              </thead>

              <tbody>
                {authLoading ? (
                  <tr>
                    <td colSpan="6" className="py-6 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                        <p className="ml-3 text-gray-700 font-medium">
                          Chargement en cours...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentData // Filtrer les demandes en attente
                    .map((form, index) => (
                      <tr
                        key={form._id}
                        // className="border-b hover:bg-gray-50"
                        className="border-b bg-white hover:bg-blue-50 hover:shadow-lg hover:scale-[1.00] transition-transform transition-shadow duration-300 ease-in-out cursor-pointer"
                        onClick={() => handleRowClick(form)}
                      >
                        <td className="px-4 py-3">
                          {form.nomFormulaire}
                          {form.periodReclamation && (
                            <>
                              {" "}
                              -{" "}
                              {new Intl.DateTimeFormat("fr-CA", {
                                year: "numeric",
                                month: "long",
                                timeZone: "UTC",
                              }).format(new Date(form.periodReclamation))}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">{form.numeroFormulaire}</td>
                        <td className="px-4 py-3">{form.nom}</td>

                        <td className="px-4 py-3">
                          {new Date(form.dateSoumission).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block w-3 h-3 rounded-full mr-2 ${getStatusDotColor(
                              form.etatDemande
                            )}`}
                          ></span>{" "}
                          {form.etatDemande}
                        </td>
                        {checkGroupRecMensuelle || checkGroupAdmin ?
                          <td className="px-4 py-3">
                            {new Date(form.traitant[form.traitant.length - 1]?.dateHeure).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })}

                          </td> : ""}

                        {(checkGroupSeancePlacement || checkGroupAdmin) && (
                          <td className="px-4 py-3">{form.lieuTravail}</td>
                        )}

                        <td className="px-4 py-3">
                          <span className="text-blue-800">
                            {form.nomFormulaire ===
                              "Réclamation mensuelle de dépenses" &&
                              form.managerInfo.etatDemande === "Approuvé" &&
                              recMensuelleGroupUserIds.includes(
                                form.traitant[form.traitant.length - 1]?.userId
                              )
                              ? // (checkGroupRecMensuelle || checkGroupAdmin)
                              "Comptes Payables : "
                              : ""}
                          </span>
                          <span>
                            {form.traitant.length > 0 &&
                              form.etatDemande !== "En attente"
                              ? form.traitant[form.traitant.length - 1].nom
                              : "—"}
                          </span>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>

            {demandeEnAttente.length > 0 && (
              <div className="mt-4 flex justify-center items-center space-x-4">
                <button
                  onClick={() => { window.scrollTo({ top: 230, behavior: "smooth" }); handlePageChange(currentPage - 1) }}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 font-medium">
                  {currentPage} sur {totalPages}
                </span>

                <button
                  onClick={() => { handlePageChange(currentPage + 1); window.scrollTo({ top: 230, behavior: "smooth" }); }}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </button>
              </div>)}
          </div> */}

          {/* Onglets par statut */}

<div className="flex gap-1.5 border-b border-gray-200 mb-6 overflow-x-auto">
  {tabs.map((tab) => {
    const count =
  tab.label === "À traiter"
    ? demandeEnAttente.filter(
        (f) =>
          f.etatDemande === "En attente" ||
          f.etatDemande === "En traitement"
      ).length
    : demandeEnAttente.filter(
        (f) => f.etatDemande === tab.label
      ).length;

    const isActive = activeTab === tab.label;
    const hasCount = count > 0;

    return (
      <button
        key={tab.label}
        onClick={() => {
          setActiveTab(tab.label);
          handlePageChange(1);
        }}
        className={`relative flex items-center gap-2 px-3.5 py-2.5 text-[14px] font-medium border-b-2 whitespace-nowrap transition-all duration-200
          ${
            isActive
              ? getActiveTabStyle(tab.label)
              : hasCount
              ? "border-transparent text-gray-700 hover:text-gray-900"
              : "border-transparent text-gray-400 hover:text-gray-500"
          }`}
      >
        <span
          className={`w-2.5 h-2.5 rounded-full ${getStatusDotColor(tab.label)} ${
            hasCount ? "opacity-100" : "opacity-45"
          }`}
        ></span>

        <span className={`${isActive ? "font-semibold" : hasCount ? "font-medium" : "font-normal"}`}>
          {tab.label}
        </span>

        <span
          className={`ml-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full transition-all duration-200
            ${
               hasCount
                ? `${getTabBadgeColor(tab.label)}`
                : "bg-gray-100 text-gray-400"
            }`}
        >
          {count}
        </span>
      </button>
    );
  })}
  <div className="ml-auto flex items-center self-center px-3 text-sm text-slate-500">
    <span>Total&nbsp;: </span>
    <span className="ml-1 font-semibold text-slate-700">
      {demandeEnAttente.length} demandes
    </span>
  </div>
</div>

{(activeTab !== "À traiter" || overridesDefaultDateRange) && (
  <div className="mb-6 mt-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-2.5 text-[14px] text-blue-800">
    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">
      i
    </div>
    <p className="leading-relaxed">{resultScopeMessage}</p>
  </div>
)}
          

          <div className="w-full min-w-0 max-w-full">
            <p id="tasks-table-scroll-hint" className="mb-2 text-xs text-slate-500 lg:hidden">
              Faites glisser le tableau horizontalement pour voir toutes les colonnes.
            </p>
           <div
             className="w-full max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-slate-200 bg-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
             role="region"
             aria-label="Tableau des tâches"
             aria-describedby="tasks-table-scroll-hint"
             tabIndex={0}
           >
  <table className="w-full min-w-[1100px] table-fixed lg:min-w-0">

    {/* HEADER */}
    <thead className="border-b border-slate-200 bg-slate-50">
      <tr className="text-sm font-semibold text-slate-700">

        <th className="w-[20%] px-5 py-3 text-left">Nom du formulaire</th>
        <th className="w-[16%] whitespace-nowrap px-5 py-3 text-left">Numéro de formulaire</th>
        <th className="w-[16%] px-5 py-3 text-left">Demandeur</th>

        {/* Date de soumission */}
        <th
          onClick={() => handleSort("submissionDate")}
          className={`w-[13%] whitespace-nowrap px-5 py-3 text-left cursor-pointer transition
            hover:text-blue-600 hover:bg-blue-50
            ${sortConfig.key === "submissionDate" ? "text-blue-600 bg-blue-50" : ""}
          `}
        >
          <div className="inline-flex items-center gap-2">
            <span>Date de soumission</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-colors ${
                sortConfig.key === "submissionDate"
                  ? "text-blue-600"
                  : "text-slate-400"
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={
                  sortConfig.key === "submissionDate" && sortConfig.order === "asc"
                    ? "M5 15l7-7 7 7"
                    : "M19 9l-7 7-7-7"
                }
              />
            </svg>
          </div>
        </th>

        {(checkGroupRecMensuelle || checkGroupAdmin) && (
          <th
            onClick={() => handleSort("processingDate")}
            className={`w-[13%] whitespace-nowrap px-5 py-3 text-left cursor-pointer transition
              hover:text-blue-600 hover:bg-blue-50
              ${sortConfig.key === "processingDate" ? "text-blue-600 bg-blue-50" : ""}
            `}
          >
            <div className="inline-flex items-center gap-2">
              <span>Date d’attribution</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-colors ${
                  sortConfig.key === "processingDate"
                    ? "text-blue-600"
                    : "text-slate-400"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d={
                    sortConfig.key === "processingDate" && sortConfig.order === "asc"
                      ? "M5 15l7-7 7 7"
                      : "M19 9l-7 7-7-7"
                  }
                />
              </svg>
            </div>
          </th>
        )}

        {(checkGroupSeancePlacement || checkGroupAdmin) && (
          <th className="w-[14%] px-5 py-3 text-left">Lieu de travail</th>
        )}

        <th className="w-[11%] whitespace-nowrap px-5 py-3 text-left">Statut</th>
        <th className="w-[13%] px-5 py-3 text-left">Assignée</th>

      </tr>
    </thead>

    {/* BODY */}
    <tbody className="divide-y divide-slate-100 text-[15px]">
      {authLoading ? (
        <tr>
          <td colSpan="6" className="py-8 text-center">
            <div className="flex items-center justify-center">
              <div className="w-6 h-6 border-4 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="ml-3 text-slate-700 font-medium text-[15px]">
                Chargement en cours...
              </p>
            </div>
          </td>
        </tr>
      ) : currentData.length === 0 ? (
        <tr>
          <td
            colSpan={
              6 +
              (checkGroupRecMensuelle || checkGroupAdmin ? 1 : 0) +
              (checkGroupSeancePlacement || checkGroupAdmin ? 1 : 0)
            }
            className="px-6 py-12 text-center"
          >
            <div className="mx-auto flex max-w-md flex-col items-center gap-2 text-slate-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-3-3v6m9-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm font-medium text-slate-600">
                {activeFiltersCount > 0
                  ? "Aucune demande ne correspond aux filtres sélectionnés."
                  : "Aucune demande à afficher dans cet onglet."}
              </p>
            </div>
          </td>
        </tr>
      ) : (
        currentData.map((form, index) => (
          <tr
            key={form._id}
            tabIndex={0}
            onKeyDown={(event) => handleRowKeyDown(event, form)}
            aria-current={derniereDemandeSelectionnee === (form.numeroFormulaire || form._id) ? "true" : undefined}
            className={`group cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 ${
              derniereDemandeSelectionnee === (form.numeroFormulaire || form._id)
                ? "bg-blue-100 hover:bg-blue-100 [&>td:first-child]:shadow-[inset_4px_0_0_0_#2563eb]"
                : "bg-white hover:bg-blue-50"
            }`}
            onClick={() => handleRowClick(form)}
          >
            {/* NOM */}
            <td className="px-5 py-2.5 font-medium text-slate-800">
              <div className="leading-5">
                <span>{form.nomFormulaire}</span>
                {form.periodReclamation && (
                  <span className="text-slate-500 font-normal">
                    {" "}
                    -{" "}
                    {new Intl.DateTimeFormat("fr-CA", {
                      year: "numeric",
                      month: "long",
                      timeZone: "UTC",
                    }).format(new Date(form.periodReclamation))}
                  </span>
                )}
              </div>
            </td>

            {/* NUMERO */}
            <td className="whitespace-nowrap px-5 py-2.5 text-slate-700">
              <span>
                {form.numeroFormulaire}
              </span>
            </td>

            {/* DEMANDEUR */}
            <td className="whitespace-normal break-words px-5 py-2.5 text-slate-700">
              {form.nom}
            </td>

            {/* DATE */}
            <td className="whitespace-nowrap px-5 py-2.5 text-slate-600">
              {new Date(form.dateSoumission).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </td>

            {/* ATTRIBUTION */}
            {checkGroupRecMensuelle || checkGroupAdmin ? (
              <td className="whitespace-nowrap px-5 py-2.5 text-slate-600">
                {formatOptionalDateTime(
                  form.traitant[form.traitant.length - 1]?.dateHeure,
                )}
              </td>
            ) : (
              ""
            )}

            {/* LIEU */}
            {(checkGroupSeancePlacement || checkGroupAdmin) && (
              <td className="whitespace-normal break-words px-5 py-2.5 text-slate-700">
                {form.lieuTravail}
              </td>
            )}

            {/* STATUT */}
            <td className="whitespace-nowrap px-5 py-2.5 text-slate-700">
              <div className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-medium text-slate-700">
                <span
                  className={`h-2 w-2 flex-shrink-0 rounded-full ${getStatusDotColor(
                    form.etatDemande
                  )}`}
                ></span>
                <span>
                  {form.etatDemande}
                </span>
              </div>
            </td>

            {/* ASSIGNÉ */}
            <td className="whitespace-normal break-words px-5 py-2.5 text-slate-700">
              {form.nomFormulaire === "Réclamation mensuelle de dépenses" &&
              form.managerInfo.etatDemande === "Approuvé" &&
              recMensuelleGroupUserIds.includes(
                form.traitant[form.traitant.length - 1]?.userId
              ) ? (
                <div className="flex flex-col items-start gap-1">
                  <span
                    title="Comptes payables"
                    className="inline-flex rounded-md border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700"
                  >
                    CP
                  </span>
                  <span>{form.traitant[form.traitant.length - 1]?.nom || "—"}</span>
                </div>
              ) : (
                <span>
                  {form.traitant.length > 0 && form.etatDemande !== "En attente"
                    ? form.traitant[form.traitant.length - 1].nom
                    : "—"}
                </span>
              )}
            </td>

          </tr>
        ))
      )}
    </tbody>

  </table>
</div>

            
            {currentData.length > 0 && (
              <div className="mt-5 flex flex-col items-center gap-3">
                <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-600">
                  <span>
                    Demandes {firstVisibleItem} à {lastVisibleItem} sur {tabSortedData.length}
                  </span>
                  <label className="inline-flex items-center gap-2">
                    <span>Afficher</span>
                    <select
                      value={itemsPerPage}
                      onChange={(event) => {
                        setItemsPerPage(Number(event.target.value));
                        handlePageChange(1);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                      aria-label="Nombre de demandes par page"
                    >
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>par page</span>
                  </label>
                </div>

                {totalPages > 1 && (
                  <nav
                    className="flex flex-wrap items-center justify-center gap-2"
                    aria-label="Pagination des tâches"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        window.scrollTo({ top: 230, behavior: "smooth" });
                        handlePageChange(currentPage - 1);
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={currentPage === 1}
                    >
                      Précédent
                    </button>

                    {getVisiblePages().map((page) =>
                      typeof page === "number" ? (
                        <button
                          key={page}
                          type="button"
                          onClick={() => {
                            handlePageChange(page);
                            window.scrollTo({ top: 230, behavior: "smooth" });
                          }}
                          className={`h-10 min-w-10 rounded-lg px-3 text-sm font-semibold transition-colors ${
                            currentPage === page
                              ? "bg-blue-600 text-white shadow-sm"
                              : "border border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                          }`}
                          aria-current={currentPage === page ? "page" : undefined}
                          aria-label={`Page ${page}`}
                        >
                          {page}
                        </button>
                      ) : (
                        <span key={page} className="px-1 text-gray-400" aria-hidden="true">
                          …
                        </span>
                      ),
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        handlePageChange(currentPage + 1);
                        window.scrollTo({ top: 230, behavior: "smooth" });
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={currentPage === totalPages}
                    >
                      Suivant
                    </button>
                  </nav>
                )}
              </div>
            )}
          
          </div>

          {/* Main Popup Modal */}

          {selectedForm && (
            <div
              className="fixed inset-0 z-50 flex justify-end overscroll-none bg-slate-950/45 backdrop-blur-[2px]"
              onMouseDown={(event) => {
                if (
                  event.target === event.currentTarget &&
                  !showReject &&
                  !showConfirm
                ) {
                  closeModal();
                }
              }}
            >
              <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="tache-details-title"
                className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-full sm:max-w-2xl sm:border-l sm:border-slate-200 lg:max-w-4xl"
              >
                <header className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2
                        id="tache-details-title"
                        className="text-lg font-semibold text-slate-900 sm:text-xl"
                      >
                        Détails de la demande
                      </h2>
                      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                        <span
                          className={`h-2 w-2 rounded-full ${getStatusDotColor(
                            selectedForm.etatDemande,
                          )}`}
                          aria-hidden="true"
                        />
                        {selectedForm.etatDemande || "État inconnu"}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm font-medium text-slate-600">
                      {selectedForm.nomFormulaire}
                    </p>
                    {selectedForm.numeroFormulaire && (
                      <p className="mt-0.5 break-all text-xs text-slate-500">
                        {selectedForm.numeroFormulaire}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                    aria-label="Fermer les détails de la demande"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </header>

                <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 py-5 sm:px-6">
                  {renderFormDetails(selectedForm)}

                {/* Commentaires */}
                {selectedForm.etatDemande === "En traitement" &&
                  selectedForm.nomFormulaire !==
                  "Réclamation mensuelle de dépenses" &&
                  selectedForm.nomFormulaire !==
                  "Demande AEFO" &&
                  selectedForm.nomFormulaire !==
                  "Demande FEESO" &&
                  selectedForm.nomFormulaire !==
                  "Demande SCFP" &&
                  selectedForm.nomFormulaire !==
                  "Achat équipement informatique" &&
                  selectedForm.nomFormulaire !==
                  "Accès hors pays"&&
                  selectedForm.nomFormulaire !==
                  "Entente de travail en mode hybrid" &&
                  (selectedForm.traitant?.at(-1)?.userId === user.id ||
                    checkGroupAdmin) && (
                    <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div>
                        <label
                          htmlFor="tache-commentaire"
                          className="mb-1.5 block text-sm font-semibold text-slate-800"
                        >
                          Commentaire de traitement
                        </label>
                      <textarea
                        id="tache-commentaire"
                        value={rejectComment}
                        onChange={(e) => {
                          setRejectComment(e.target.value);
                          if (e.target.value.trim()) setErrorMessage("");
                        }}
                        placeholder="Ajoutez un commentaire..."
                        rows={3}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                      {errorMessage && (
                          <p className="mt-1.5 text-sm text-red-600">{errorMessage}</p>
                      )}
                      </div>

                      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_11rem] sm:items-center">
                        <label
                          htmlFor="visibilite-commentaire"
                          className="text-sm font-medium text-slate-700"
                        >
                          Souhaitez-vous que le demandeur voie ce commentaire ?
                        </label>
                        <select
                          id="visibilite-commentaire"
                          value={showComment}
                          onChange={(e) => {
                            setShowComment(e.target.value);
                            if (e.target.value.trim()) setErrorShowComment("");
                          }}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="oui">Oui</option>
                          <option value="non">Non</option>
                        </select>
                      </div>
                      {errorShowComment && (
                        <p className="text-sm text-red-600">
                          {errorShowComment}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions principales */}
                {!showReject && !showConfirm && (
                  <footer className="flex flex-none flex-col-reverse gap-3 border-t border-slate-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  {/* En traitement */}
                  {selectedForm.etatDemande === "En traitement" &&
                    selectedForm.nomFormulaire &&
                    selectedForm.nomFormulaire !==
                    "Réclamation mensuelle de dépenses" &&
                    selectedForm.nomFormulaire !==
                    "Demande AEFO" &&
                    selectedForm.nomFormulaire !==
                    "Demande FEESO" &&
                    selectedForm.nomFormulaire !==
                    "Demande SCFP" &&
                    selectedForm.nomFormulaire !==
                    "Achat équipement informatique" &&
                    selectedForm.nomFormulaire !==
                    "Accès hors pays" &&
                    selectedForm.nomFormulaire !==
                    "Entente de travail en mode hybrid" &&
                    (selectedForm.traitant?.at(-1)?.userId === user.id ||
                      checkGroupAdmin) && (
                      <>
                        <button
                          type="button"
                          className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 sm:w-auto"
                          onClick={() => setshowReject(true)}
                        >
                          Rejeter
                        </button>
                        <button
                          type="button"
                          className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 sm:w-auto"
                          onClick={handleReassignClick}
                        >
                          Réassigner
                        </button>
                        <button
                          type="button"
                          className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 sm:w-auto"
                          onClick={() => setshowConfirm(true)}
                        >
                          Approuver
                        </button>
                      </>
                    )}

                  {/* En attente */}
                  {selectedForm.etatDemande === "En attente" && (
                    <button
                      type="button"
                      onClick={() =>
                        changeRequest(
                          selectedForm._id,
                          selectedForm.nomFormulaire,
                          "En traitement",
                          selectedForm.courriel,
                          selectedForm.numeroFormulaire,
                          true
                        )
                      }
                      className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 sm:w-auto"
                    >
                      Prendre en charge
                    </button>
                  )}

                  {(selectedForm.nomFormulaire === "Demande de projet") && 
                  (!selectedForm.estFerme) && 
                  (
                      <button
                        type="button"
                        onClick={() => handleCloturerClick(selectedForm._id)}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-blue-700 bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Clôturer la demande
                      </button>
                  )}

                  {/* Réclamation mensuelle */}
                  {(selectedForm.nomFormulaire ===
                    "Réclamation mensuelle de dépenses" || selectedForm.nomFormulaire ===
                    "Demande AEFO" || selectedForm.nomFormulaire ===
                    "Demande FEESO" || selectedForm.nomFormulaire ===
                    "Demande SCFP" || selectedForm.nomFormulaire ===
                    "Achat équipement informatique" || selectedForm.nomFormulaire ===
                    "Accès hors pays" || selectedForm.nomFormulaire ===
                    "Demande de projet" || selectedForm.nomFormulaire ===
                    "Entente de travail en mode hybrid") && selectedForm.etatDemande !== "En attente" && (
                      <button
                        type="button"
                        onClick={() => handleContinueClick(true)}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-800 bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Consulter
                      </button>
                    )}
                    </div>

                    <button
                      type="button"
                      onClick={closeModal}
                      className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
                    >
                      Fermer
                    </button>
                  </footer>
                )}

                {/* Confirmation Rejet */}
                {showReject && (
                  <footer className="flex-none border-t border-red-100 bg-red-50 px-4 py-4 sm:px-6">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-red-100 text-red-700">
                        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Confirmer le rejet
                        </h3>
                        <p className="mt-1 text-sm leading-5 text-slate-600">
                          La demande sera retournée avec le statut « Rejeté ».
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setshowReject(false);
                          setRejectComment("");
                          setShowComment("");
                          setErrorMessage("");
                          setErrorShowComment("");
                        }}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          changeRequest(
                            selectedForm._id,
                            selectedForm.nomFormulaire,
                            "Rejeté",
                            selectedForm.courriel,
                            selectedForm.numeroFormulaire
                          );
                          setshowReject(false);
                        }}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-red-700 bg-red-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Rejeter la demande
                      </button>
                    </div>
                  </footer>
                )}

                {/* Confirmation Approve */}
                {showConfirm && (
                  <footer className="flex-none border-t border-emerald-100 bg-emerald-50 px-4 py-4 sm:px-6">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Confirmer l’approbation
                        </h3>
                        <p className="mt-1 text-sm leading-5 text-slate-600">
                          Voulez-vous approuver cette demande?
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={() => setshowConfirm(false)}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          changeRequest(
                            selectedForm._id,
                            selectedForm.nomFormulaire,
                            "Approuvé",
                            selectedForm.courriel,
                            selectedForm.numeroFormulaire
                          );
                          setshowConfirm(false);
                        }}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 sm:w-auto"
                      >
                        Approuver la demande
                      </button>
                    </div>
                  </footer>
                )}
              </section>
            </div>
          )}

          {/* Popup pour Réassigner */}
          {showReassignPopup && (
            <div
              className="fixed inset-0 z-[60] flex items-end justify-center overscroll-none bg-slate-950/55 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  handleReassignCancel();
                }
              }}
            >
              <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="reassign-title"
                className="flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)] sm:max-w-lg sm:rounded-2xl"
              >
                <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-6">
                  <div className="min-w-0">
                    <h2
                      id="reassign-title"
                      className="text-lg font-semibold text-slate-900 sm:text-xl"
                    >
                      Réassigner la tâche
                    </h2>
                    <p className="mt-1 text-sm leading-5 text-slate-600">
                      Choisissez la personne qui poursuivra le traitement de cette demande.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleReassignCancel}
                    className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                    aria-label="Fermer la fenêtre de réassignation"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </header>

                <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 py-5 sm:px-6">
                  <div className="[&_label]:mb-1.5 [&_label]:block [&_label]:text-sm [&_label]:font-semibold [&_label]:text-slate-800 [&_select]:min-h-11 [&_select]:w-full [&_select]:rounded-lg [&_select]:border [&_select]:border-slate-300 [&_select]:bg-white [&_select]:px-3 [&_select]:py-2.5 [&_select]:text-sm [&_select]:text-slate-800 [&_select]:shadow-sm [&_select]:outline-none focus-within:[&_select]:border-blue-500 focus-within:[&_select]:ring-2 focus-within:[&_select]:ring-blue-100">
                    <ReassignerGroup
                      nomFormulaire={selectedForm.nomFormulaire}
                      onUserSelect={handleUserSelect}
                    />
                  </div>

                  {errorReassign && (
                    <p className="mt-1.5 text-sm font-medium text-red-600">
                      {errorReassign}
                    </p>
                  )}

                  {nomUser ? (
                    <div className="mt-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-100 text-blue-700">
                        <UserRoundCheck className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                          Nouveau responsable
                        </p>
                        <p className="mt-1 break-words text-sm font-semibold text-slate-900">
                          {nomUser}
                        </p>
                        {emailUser && (
                          <p className="mt-0.5 break-all text-sm text-slate-600">
                            {emailUser}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm leading-5 text-slate-500">
                      Le nom et le courriel du nouveau responsable apparaîtront ici après la sélection.
                    </p>
                  )}
                </div>

                <footer className="flex flex-none flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
                  <button
                    type="button"
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
                    onClick={handleReassignCancel}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-blue-700 bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-600 sm:w-auto"
                    onClick={() =>
                      handleReassignConfirm(
                        selectedForm._id,
                        selectedForm.nomFormulaire
                      )
                    }
                    disabled={!idUser}
                  >
                    Réassigner
                  </button>
                </footer>
              </section>
            </div>
          )}
          {isLoading && (
            <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
              <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
              <p className="ml-3 text-gray-700 font-medium">
                Chargement en cours...
              </p>
            </div>
          )}

          {isAccepted && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
              <div
                className={`flex items-center space-x-3 px-5 py-4 bg-white ${message.color} rounded-lg shadow-xl transform transition-all duration-500 ease-in-out animate-bounce`}
              >
                <CheckCircleIcon
                  className={`h-8 w-8 ${message.color} animate-pulse`}
                />
                <span className="text-lg font-semibold">{message.message}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TachesEnAttente;










  // const renderFormDetails = (form) => (
  //   <div className="space-y-6">
  //     <div className="bg-gray-50 p-4 rounded-xl shadow-sm border border-gray-200 space-y-2">
  //       <p>
  //         <strong>Formulaire:</strong> {form.nomFormulaire}
  //       </p>
  //       <p>
  //         <strong>Numéro de formulaire:</strong> {form.numeroFormulaire}
  //       </p>
  //       <p>
  //         <strong>Nom:</strong> {form.nom}
  //       </p>
  //       {form.courriel && (
  //         <p>
  //           <strong>Courriel: </strong> {form.courriel}
  //         </p>
  //       )}
  //       {form.poste && (
  //         <p>
  //           <strong>Poste: </strong> {form.poste}
  //         </p>
  //       )}
  //       {form.matricule && (
  //         <p>
  //           <strong>Matricule Centrale: </strong> {form.matricule}
  //         </p>
  //       )}

  //       <p>
  //         <strong>Date de soumission:</strong>{" "}
  //         {new Date(form.dateSoumission).toLocaleString("fr-FR", {
  //           day: "2-digit",
  //           month: "2-digit",
  //           year: "numeric",
  //           hour: "2-digit",
  //           minute: "2-digit",
  //           hour12: false,
  //         })}
  //       </p>
  //       <p>
  //         <strong>État de la demande:</strong> {form.etatDemande}
  //       </p>
  //       {form.pasReconnuCoupableDepuisDerniereDeclaration && (
  //         <p>
  //           <strong>Pas reconnu coupable depuis derniere declaration : </strong>{" "}
  //           {form.pasReconnuCoupableDepuisDerniereDeclaration}
  //         </p>
  //       )}
  //       {form.reconnuCoupableDepuisDerniereDeclaration && (
  //         <p>
  //           <strong>Reconnu coupable depuis derniere declaration : </strong>{" "}
  //           {form.reconnuCoupableDepuisDerniereDeclaration}
  //         </p>
  //       )}
  //       {form.declarationExacteEtComplete && (
  //         <p>
  //           <strong>Declaration exacte et complete : </strong>{" "}
  //           {form.declarationExacteEtComplete}
  //         </p>
  //       )}
  //       {form.dateDeclaration && (
  //         <p>
  //           <strong>Date de déclaration :</strong>{" "}
  //           {form.dateDeclaration.slice(0, 10)}
  //         </p>
  //       )}
  //       {form.dispositions && form.dispositions.length > 0 && (
  //         <div>
  //           <strong>Dispositions :</strong>
  //           {form.dispositions.map((disposition, index) => (
  //             <div key={index} style={{ marginLeft: "20px", marginTop: "5px" }}>
  //               <p>
  //                 <strong>Infractions :</strong> {disposition.infractions}
  //               </p>
  //               <p>
  //                 <strong>Date :</strong> {disposition.date}
  //               </p>
  //               <p>
  //                 <strong>Tribunal :</strong> {disposition.tribunal}
  //               </p>
  //               <p>
  //                 <strong>Commentaire :</strong> {disposition.commentaire}
  //               </p>
  //             </div>
  //           ))}
  //         </div>
  //       )}

  //       {form.nomMembre && (
  //         <p>
  //           <strong>Nom du membre du personnel: </strong> {form.nomMembre}
  //         </p>
  //       )}
  //       {form.prenomMembre && (
  //         <p>
  //           <strong>Prénom du membre du personnel: </strong> {form.prenomMembre}
  //         </p>
  //       )}
  //       {form.posteMembre && (
  //         <p>
  //           <strong>Poste du membre du personnel: </strong> {form.posteMembre}
  //         </p>
  //       )}
  //       {form.lieuTravail && (
  //         <p>
  //           <strong>Lieu de travail: </strong> {form.lieuTravail}
  //         </p>
  //       )}
  //       {form.adressePostalResidentielle === "non" && (
  //         <p className="mt-3 block text-md font-medium text-blue-900">
  //           <strong>Adresse Résidentielle </strong>
  //         </p>
  //       )}
  //       {form.adresse && (
  //         <p>
  //           <strong>Adresse: </strong> {form.adresse}
  //         </p>
  //       )}
  //       {form.numeroappartement && (
  //         <p>
  //           <strong>Appartement: </strong> {form.numeroappartement}
  //         </p>
  //       )}
  //       {form.ville && (
  //         <p>
  //           <strong>Ville: </strong> {form.ville}
  //         </p>
  //       )}
  //       {form.province && (
  //         <p>
  //           <strong>Province: </strong> {form.province}
  //         </p>
  //       )}
  //       {form.codePostal && (
  //         <p>
  //           <strong>Code postal: </strong> {form.codePostal}
  //         </p>
  //       )}
  //       {form.adressePostalResidentielle === "non" && (
  //         <p className="mt-3 block text-md font-medium text-blue-900">
  //           <strong>Adresse Postale </strong>
  //         </p>
  //       )}

  //       {form.adressePostalResidentielle === "non" && form.boitePostale && (
  //         <p>
  //           <strong>Boite Postale: </strong> {form.boitePostale}
  //         </p>
  //       )}

  //       {form.adressePostalResidentielle === "non" &&
  //         form.appartementPostale && (
  //           <p>
  //             <strong>Appartement / unité : </strong> {form.appartementPostale}
  //           </p>
  //         )}

  //       {form.adressePostalResidentielle === "non" && form.villePostale && (
  //         <p>
  //           <strong>Ville: </strong> {form.villePostale}
  //         </p>
  //       )}
  //       {form.adressePostalResidentielle === "non" && form.provincePostale && (
  //         <p>
  //           <strong>Province: </strong> {form.provincePostale}
  //         </p>
  //       )}
  //       {form.adressePostalResidentielle === "non" &&
  //         form.codePostalPostale && (
  //           <p>
  //             <strong>Code Postal: </strong> {form.codePostalPostale}
  //           </p>
  //         )}

  //       {form.telephone && (
  //         <p className="mt-3">
  //           <strong>Numéro de téléphone principal: </strong>{" "}
  //           {`${form.telephone.slice(0, 3)} ${form.telephone.slice(
  //             3,
  //             6
  //           )} ${form.telephone.slice(6, 10)}`}
  //         </p>
  //       )}

  //       {form.telephoneCellulaire && (
  //         <p>
  //           <strong>Autre numéro de téléphone (optionnel): </strong>{" "}
  //           {`${form.telephoneCellulaire.slice(
  //             0,
  //             3
  //           )} ${form.telephoneCellulaire.slice(
  //             3,
  //             6
  //           )} ${form.telephoneCellulaire.slice(6, 10)}`}
  //         </p>
  //       )}
  //       {form.dateEntreeEnVigueur && (
  //         <p>
  //           <strong>Date d'entree en vigueur: </strong>{" "}
  //           {form.dateEntreeEnVigueur}
  //         </p>
  //       )}
  //       {form.dateDebut && (
  //         <p>
  //           <strong>Date de début: </strong> {form.dateDebut}
  //         </p>
  //       )}
  //       {form.dateFin && (
  //         <p>
  //           <strong>Date de fin: </strong> {form.dateFin}
  //         </p>
  //       )}
  //       {form.courrielSuperviseur && (
  //         <p>
  //           <strong>Courriel du superviseur: </strong>{" "}
  //           {form.courrielSuperviseur}
  //         </p>
  //       )}
  //       {form.commentairesDemandeur && (
  //         <p>
  //           <strong>Commentaires du demandeur: </strong>{" "}
  //           {form.commentairesDemandeur}
  //         </p>
  //       )}
  //       {form.nouveauNom && (
  //         <p>
  //           <strong>Nouveau Nom : </strong> {form.nouveauNom}
  //         </p>
  //       )}
  //       {form.nouveauPrenom && (
  //         <p>
  //           <strong>Nouveau Prénom : </strong> {form.nouveauPrenom}
  //         </p>
  //       )}
  //       {form.deuxiemePrenom && (
  //         <p>
  //           <strong>Deuxième Prénom : </strong> {form.deuxiemePrenom}
  //         </p>
  //       )}
  //       {form.institution && (
  //         <p>
  //           <strong>Institution : </strong> {form.institution}
  //         </p>
  //       )}
  //       {form.banque && (
  //         <p>
  //           <strong>Banque : </strong> {form.banque}
  //         </p>
  //       )}
  //       {form.numeroTransit && (
  //         <p>
  //           <strong>Numéro de transit : </strong> {form.numeroTransit}
  //         </p>
  //       )}
  //       {form.numeroCompte && (
  //         <p>
  //           <strong>Numéro de compte : </strong> {form.numeroCompte}
  //         </p>
  //       )}
  //       {/* {form.commentaire && (
  //       <p>
  //         <strong>commentaire : </strong> {form.commentaire}
  //       </p>
  //     )} */}
  //       {form.periodReclamation && (
  //         <p>
  //           <strong>Période de la réclamation :</strong>{" "}
  //           {new Intl.DateTimeFormat("fr-CA", {
  //             year: "numeric",
  //             month: "long",
  //             timeZone: "UTC", // <=== force UTC
  //           }).format(new Date(form.periodReclamation))}
  //         </p>
  //       )}

  //       {form.documentJustificatif && (
  //         <p>
  //           <strong>document justificatif : </strong>{" "}
  //           {form.documentJustificatif}
  //         </p>
  //       )}

  //       {form.uploadedFile && (
  //         <div className="mt-3 w-3/4 flex items-center space-x-4">
  //           <p
  //             className="border rounded-lg px-4 py-2 w-[50%] bg-blue-900 text-white cursor-pointer inline-block hover:bg-blue-700 transition duration-200"
  //             onClick={() => setShowPopup(true)}
  //           >
  //             Voir le document : {form.uploadedFile.filename}
  //           </p>
  //         </div>
  //       )}
  //       {showPopup && form.uploadedFile?.data && (
  //         <>
  //           <div
  //             className="fixed inset-0 bg-black bg-opacity-50 z-40"
  //             onClick={closePopup}
  //           ></div>
  //           <div className="fixed w-[60%] h-[80%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-6 bg-white border shadow-lg rounded-lg z-50">
  //             <div className="flex justify-between items-center">
  //               <div>
  //                 <h3 className="text-xl font-semibold text-gray-800 mb-2">
  //                   Aperçu du fichier
  //                 </h3>
  //                 {form.uploadedFile.contentType?.startsWith("image/") && (
  //                   <div className="space-y-2">
  //                     <p className="text-sm text-gray-500 flex items-center gap-2">
  //                       Télécharger :
  //                       <a
  //                         href={`data:${form.uploadedFile.contentType};base64,${form.uploadedFile.data}`}
  //                         download
  //                         className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition"
  //                         title="Télécharger l'image"
  //                       >
  //                         <Download className="w-4 h-4 text-gray-700" />
  //                       </a>
  //                     </p>
  //                     <p className="text-sm text-gray-500">
  //                       Cliquez sur l’image pour l’agrandir
  //                     </p>
  //                   </div>
  //                 )}
  //               </div>
  //               <button
  //                 onClick={closePopup}
  //                 className="absolute top-2 right-4 ml-4 text-gray-500 hover:text-gray-700 font-bold text-xl transition"
  //                 aria-label="Fermer"
  //               >
  //                 X
  //               </button>
  //             </div>

  //             {/* Image preview */}
  //             {form.uploadedFile.contentType?.startsWith("image/") && (
  //               <SlideshowLightbox className="mx-auto">
  //                 <img
  //                   src={`data:${form.uploadedFile.contentType};base64,${form.uploadedFile.data}`}
  //                   alt="Aperçu"
  //                   className="max-w-full max-h-[500px] rounded-lg border cursor-zoom-in"
  //                 />
  //               </SlideshowLightbox>
  //             )}

  //             {/* PDF preview */}
  //             {form.uploadedFile.contentType === "application/pdf" && (
  //               <iframe
  //                 src={`data:application/pdf;base64,${form.uploadedFile.data}`}
  //                 title="PDF preview"
  //                 className="w-[100%] h-[90%] border mt-4"
  //               />
  //             )}
  //           </div>
  //         </>
  //       )}

  //       {form.commentaire && (
  //         <p className="mt-4 text-lg text-gray-700">
  //           <strong
  //             className={
  //               form.etatDemande === "Approuvé"
  //                 ? "text-green-600"
  //                 : form.etatDemande === "Rejeté"
  //                   ? "text-red-600"
  //                   : "text-gray-600"
  //             }
  //           >
  //             Note du traiteur :
  //           </strong>{" "}
  //           {form.commentaire}
  //         </p>
  //       )}

  //       {form.traitant?.length > 0 && (
  //         <div className="mt-8">
  //           <h3 className="text-lg font-semibold text-gray-800 mb-2">
  //             Traitants :
  //           </h3>
  //           <table className="w-full border border-gray-300 rounded-lg overflow-hidden ">
  //             <thead className="bg-gray-200">
  //               <tr>
  //                 <th className="p-3 text-left font-medium w-1/5">Nom</th>
  //                 <th className="p-3 text-left font-medium w-1/4">Email</th>
  //                 <th className="p-3 text-left font-medium w-1/6">État</th>
  //                 <th className="p-3 text-left font-medium w-1/5">
  //                   Date et heure
  //                 </th>
  //                 {form.nomFormulaire !==
  //                   "Réclamation mensuelle de dépenses" && (
  //                     <th className="p-3 text-left font-medium w-1/6">Notes</th>
  //                   )}
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {
  //                 // form.traitant.map((t, index) => (
  //                 form.traitant
  //                   ?.filter((t, index, arr) => {
  //                     // On cache la ligne "Enregistrer"
  //                     if (
  //                       t.etatDemande === "Enregistrer" &&
  //                       arr[index + 1]?.etatDemande === "En traitement"
  //                     ) {
  //                       return false;
  //                     }

  //                     // On cache la ligne "En traitement" qui suit "Enregistrer"
  //                     if (
  //                       t.etatDemande === "En traitement" &&
  //                       arr[index - 1]?.etatDemande === "Enregistrer"
  //                     ) {
  //                       return false;
  //                     }

  //                     return true;
  //                   })
  //                   .map((t, index) => (
  //                     <tr key={index} className={`bg-white `}>
  //                       <td className="border-t px-3 py-2 break-words">{t.nom}</td>
  //                       <td className="border-t px-3 py-2 break-words">
  //                         {t.email}
  //                       </td>
  //                       <td className="border-t px-3 py-2">{t.etatDemande}</td>
  //                       <td className="border-t px-3 py-2">
  //                         {formatDate(t.dateHeure)}
  //                       </td>
  //                       {form.nomFormulaire !==
  //                         "Réclamation mensuelle de dépenses" && (
  //                           <td className="border-t px-3 py-2 max-w-xs break-words whitespace-pre-wrap overflow-y-auto">{t.commentaire}</td>
  //                         )}

  //                     </tr>
  //                   ))
  //               }
  //             </tbody>
  //           </table>
  //         </div>
  //       )}
  //     </div>
  //   </div>
  // );
